import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Navigate, useNavigate } from 'react-router-dom';

import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { fetchTicketQr } from '../lib/api';
import { formatTime, pickI18n } from '../lib/format';
import { useKiosk } from '../lib/session';
import { ticketTypeLabel } from '../lib/ticket-label';

/** 出票完成 — Flow 3.12。 */
export function IssueSuccessScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, locale, draft, resetSession } = useKiosk();
  const { product, schedule, delivery, buyerEmail, issuedTicketIds, order } = draft;
  const firstTicketId = issuedTicketIds[0];

  // 票券 QR 為盡力而為：服務帳號無權 / 離線時回 null，畫面降級為無 QR 顯示。
  const qrQuery = useQuery({
    queryKey: ['ticket-qr', firstTicketId],
    queryFn: () => fetchTicketQr(firstTicketId ?? ''),
    enabled: firstTicketId !== undefined,
    retry: false,
  });

  if (order === null || product === null || schedule === null) {
    return <Navigate to="/" replace />;
  }

  const deliveryLine =
    delivery === 'email'
      ? t('done.emailSent', { email: buyerEmail })
      : delivery === 'line'
        ? t('done.lineSent')
        : t('done.paperPrinting');

  return (
    <KioskFrame center>
      <h1 className="k-h1" style={{ color: 'var(--c-success)' }}>
        ✅ {t('done.title')}
      </h1>

      <div className="k-card" style={{ width: '100%', textAlign: 'left' }}>
        <div className="k-h2">{pickI18n(product.name_i18n, product.sku, locale)}</div>
        <p className="k-text k-text--dim">
          {formatTime(schedule.departure_at)}
          {Object.entries(draft.quantities)
            .filter(([, qty]) => qty > 0)
            .map(([tt, qty]) => ` · ${ticketTypeLabel(tt, null, locale)} ${qty}`)
            .join('')}
        </p>
      </div>

      <p className="k-text" style={{ fontWeight: 700, marginTop: 8 }}>
        {t('done.delivering')}
      </p>
      <p className="k-text k-text--dim">{deliveryLine}</p>

      {qrQuery.data && (
        <div className="k-card" style={{ marginTop: 16 }}>
          <p className="k-text" style={{ fontWeight: 700, marginBottom: 12 }}>
            {t('done.boardingQr')}
          </p>
          <QRCodeSVG value={qrQuery.data.qr_payload} size={280} level="M" />
          <p className="k-text k-text--dim" style={{ marginTop: 12 }}>
            {qrQuery.data.ticket_code}
          </p>
        </div>
      )}

      <p className="k-text k-text--dim" style={{ marginTop: 16 }}>
        ⓘ {t('done.keepQr')}
      </p>

      <KioskButton
        variant="primary"
        large
        style={{ marginTop: 28 }}
        onClick={() => {
          resetSession();
          navigate('/', { replace: true });
        }}
      >
        {t('done.finish')}
      </KioskButton>
    </KioskFrame>
  );
}
